/**
 * The one ApiClient instance, the session token store, query keys and the
 * React Query hooks shared across pages. Pages call `api.*` for mutations and
 * use the hooks here for reads, so cache keys stay consistent.
 */
import { keepPreviousData, QueryClient, useQuery } from '@tanstack/react-query';
import {
  ApiClient,
  ApiError,
  type CorrectionStatus,
  type DeviceKind,
  type EmployeeStatus,
  type ExceptionStatus,
  type ExceptionType,
} from '@em/shared';

const TOKEN_KEY = 'em.portal.token';

export const tokenStore = {
  get: (): string | null => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (t: string) => sessionStorage.setItem(TOKEN_KEY, t),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};

/** Fired when the API rejects a stored token (expired, revoked or idle for 8 hours). */
export const SESSION_EXPIRED_EVENT = 'em:session-expired';

export const api = new ApiClient({
  baseUrl: '',
  getToken: () => tokenStore.get(),
  onUnauthorized: () => {
    if (!tokenStore.get()) return;
    tokenStore.clear();
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  },
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});

/* --------------------------------------------------------------- errors */

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof TypeError) return 'Could not reach the server. Check your connection and try again.';
  // ApiClient parses every body as JSON; a proxy or gateway error page is not JSON.
  if (e instanceof SyntaxError) return 'The server is not responding properly (it may be restarting). Try again in a moment.';
  if (e instanceof Error) return e.message;
  return 'Something went wrong';
}

/** Extra lines the API attaches to some refusals (lock blockers, preflight problems). */
export function errorDetails(e: unknown): string[] {
  if (!(e instanceof ApiError) || !Array.isArray(e.details)) return [];
  if (e.code === 'bad_request') return []; // validation issues are already in the message
  return e.details
    .map((d) => (typeof d === 'string' ? d : d && typeof d === 'object' && 'message' in d ? String((d as { message: unknown }).message) : null))
    .filter((d): d is string => !!d);
}

export const isApiError = (e: unknown, code?: string): e is ApiError => e instanceof ApiError && (!code || e.code === code);

/* ----------------------------------------------------------- query keys */

export const qk = {
  session: ['session'] as const,
  settings: ['settings'] as const,
  employees: (q?: object) => ['employees', q ?? {}] as const,
  employee: (id: string) => ['employee', id] as const,
  employeeDevices: (id: string) => ['employee', id, 'devices'] as const,
  employeeTimeline: (id: string) => ['employee', id, 'timeline'] as const,
  ruleAssignments: (id: string) => ['employee', id, 'rule-assignments'] as const,
  teams: ['teams'] as const,
  sites: ['sites'] as const,
  users: ['users'] as const,
  devices: (kind?: DeviceKind) => ['devices', kind ?? 'all'] as const,
  ruleTemplates: ['rule-templates'] as const,
  shiftTemplates: ['shift-templates'] as const,
  roster: (q: object) => ['roster', q] as const,
  live: ['live'] as const,
  events: (q: object) => ['events', q] as const,
  corrections: (q?: object) => ['corrections', q ?? {}] as const,
  exceptions: (q?: object) => ['exceptions', q ?? {}] as const,
  periods: ['pay-periods'] as const,
  period: (id: string) => ['pay-period', id] as const,
  timesheet: (id: string) => ['timesheet', id] as const,
  preflight: (id: string, method: string) => ['export', id, 'preflight', method] as const,
  preview: (id: string) => ['export', id, 'preview'] as const,
  batches: (periodId?: string) => ['export-batches', periodId ?? 'all'] as const,
  categories: ['categories'] as const,
  myobStatus: ['myob', 'status'] as const,
  myobCategories: ['myob', 'categories'] as const,
  myobJobs: ['myob', 'jobs'] as const,
  myobFiles: ['myob', 'company-files'] as const,
  report: (kind: string, q: object) => ['report', kind, q] as const,
  audit: (q: object) => ['audit', q] as const,
  holidays: (q: object) => ['holidays', q] as const,
  privacy: ['privacy-notices'] as const,
  jobs: ['jobs'] as const,
};

/** Invalidate everything a timesheet-changing action may affect. */
export function invalidateAttendance() {
  for (const k of ['exceptions', 'corrections', 'pay-periods', 'pay-period', 'timesheet', 'events', 'live', 'export', 'report']) {
    void queryClient.invalidateQueries({ queryKey: [k] });
  }
}

/* ---------------------------------------------------------------- hooks */

export const useSettings = () => useQuery({ queryKey: qk.settings, queryFn: api.settings, staleTime: 60_000 });
export const useEmployees = (q: { status?: EmployeeStatus | 'all'; teamId?: string; q?: string } = {}) =>
  useQuery({ queryKey: qk.employees(q), queryFn: () => api.employees(q), placeholderData: keepPreviousData });
export const useEmployee = (id: string) => useQuery({ queryKey: qk.employee(id), queryFn: () => api.employee(id) });
export const useTeams = () => useQuery({ queryKey: qk.teams, queryFn: api.teams, staleTime: 60_000 });
export const useSites = () => useQuery({ queryKey: qk.sites, queryFn: api.sites, staleTime: 60_000 });
export const useUsers = (enabled = true) => useQuery({ queryKey: qk.users, queryFn: api.users, enabled, staleTime: 60_000 });
export const useDevices = (kind?: DeviceKind, enabled = true) => useQuery({ queryKey: qk.devices(kind), queryFn: () => api.devices({ kind }), enabled });
export const useRuleTemplates = () => useQuery({ queryKey: qk.ruleTemplates, queryFn: api.ruleTemplates });
export const useShiftTemplates = () => useQuery({ queryKey: qk.shiftTemplates, queryFn: api.shiftTemplates });
export const useCategories = () => useQuery({ queryKey: qk.categories, queryFn: api.categories, staleTime: 60_000 });
export const useJobs = (enabled = true) => useQuery({ queryKey: qk.jobs, queryFn: () => api.jobs(), enabled, staleTime: 60_000 });
export const usePeriods = () => useQuery({ queryKey: qk.periods, queryFn: api.payPeriods });
export const usePeriod = (id: string) => useQuery({ queryKey: qk.period(id), queryFn: () => api.payPeriod(id) });
export const useTimesheet = (id: string) => useQuery({ queryKey: qk.timesheet(id), queryFn: () => api.timesheet(id) });
export const useLive = () => useQuery({ queryKey: qk.live, queryFn: api.live, refetchInterval: 30_000, refetchIntervalInBackground: false });
export const useEvents = (q: { employeeId?: string; from: string; to: string }, enabled = true) =>
  useQuery({ queryKey: qk.events(q), queryFn: () => api.events(q), enabled });
export const useCorrections = (q: { status?: CorrectionStatus; employeeId?: string } = {}, enabled = true) =>
  useQuery({ queryKey: qk.corrections(q), queryFn: () => api.corrections(q), enabled });
export const useExceptions = (q: { status?: ExceptionStatus | 'all'; payPeriodId?: string; type?: ExceptionType; employeeId?: string } = {}) =>
  useQuery({ queryKey: qk.exceptions(q), queryFn: () => api.exceptions(q), placeholderData: keepPreviousData });
/** Messages for the signed-in portal user; polled so new reminders show up. */
export const useMessages = () => useQuery({ queryKey: ['messages'], queryFn: () => api.userNotifications(), refetchInterval: 60_000 });
export const useMyobStatus = (enabled = true) => useQuery({ queryKey: qk.myobStatus, queryFn: api.myobStatus, enabled, retry: false });
